<script setup>
  import {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    Switch,
    SwitchGroup,
    SwitchLabel,
    ComboboxButton
  } from '@headlessui/vue'
  import { watchThrottled, onClickOutside } from '@vueuse/core'

  const client = useStrapiClient()

  const { filters, advancedTab, sectors, employerTypes, sticky, createJobSearchUrl, categories } = defineProps({
    sticky: {
      type: Boolean,
      default: true
    },
    advancedTab: {
      type: Boolean,
      default: true
    },
    filters: {
      type: Object,
      required: true,
      default: () => ({
        search: '',
        region: [],
        sector: [],
        employerType: [],
        teleworkable: false,
        management: false,
        type: false,
        category: [],
        location: null,
        radius: 0
      })
    },
    sectors: {
      type: Object,
      required: false,
      default: () => ({
        data: []
      })
    },
    employerTypes: {
      type: Object,
      required: false,
      default: () => ({
        data: []
      })
    },
    createJobSearchUrl: {
      type: Function,
      required: false,
      default: null
    },
    categories: {
      type: Object,
      required: false,
      default: () => ({
        data: []
      })
    }
  })

  const advancedSearch = ref(filters.employerType?.length || filters.teleworkable || filters.management || filters.type || filters.category?.length)

  const { y } = useWindowScroll()
  const filtersElem = ref(null)

  const isSearchBig = computed(() => {
    return !sticky || y.value < 190
  })

  const citySuggestions = ref([])
  const cityQuery = ref('')

  const searchLocations = async query => {
    if (!query || query.length < 2) {
      citySuggestions.value = []
      return
    }

    citySuggestions.value = await client(`/locations/search?q=${encodeURIComponent(query)}`)
  }

  watchThrottled(
    cityQuery,
    newQuery => {
      searchLocations(newQuery)
    },
    { throttle: 500 }
  )

  const showRadiusDropdown = ref(false)
  const tempRadius = ref(filters.radius || 0)
  const cityDropdownRef = ref(null)
  const isCityInputFocused = ref(false)

  onClickOutside(cityDropdownRef, () => {
    showRadiusDropdown.value = false
    isCityInputFocused.value = false
  })

  const clearCityAndRadius = () => {
    filters.location = null
    filters.radius = 0
    showRadiusDropdown.value = false
  }

  watch(
    () => filters.location,
    newValue => {
      if (newValue) {
        showRadiusDropdown.value = newValue.type === 'city'
        tempRadius.value = filters.radius || 0
      }
    }
  )

  const handleCityInputFocus = () => {
    isCityInputFocused.value = true
    if (filters.location) {
      showRadiusDropdown.value = filters.location.type === 'city'
      tempRadius.value = filters.radius || 0
    }
  }

  const handleCityInputBlur = () => {
    isCityInputFocused.value = false
  }

  const handleCityInputKeydown = event => {
    if (event.key === 'Enter' && cityQuery.value.trim()) {
      event.preventDefault()
      if (createJobSearchUrl) {
        navigateTo(createJobSearchUrl())
      }
    }
  }

  const confirmRadius = () => {
    filters.radius = tempRadius.value
    showRadiusDropdown.value = false
  }
</script>

<template>
  <div
    ref="filtersElem"
    :class="[
      ' w-full transition-[top] duration-300',
      isSearchBig ? 'bg-white md:top-20' : 'fixed bottom-0 z-40 rounded-t-xl bg-tarawera-900 shadow md:bottom-auto md:top-16 md:rounded-t-none'
    ]"
  >
    <div
      :class="[
        'mx-auto flex flex-col px-4 transition-none md:flex-row md:items-center md:justify-center',
        isSearchBig ? 'max-w-screen-lg space-y-8 py-8 md:space-y-0' : 'max-w-screen-xl py-0'
      ]"
    >
      <div :class="['self-center empty:hidden max-md:text-center md:mr-8 md:basis-[35%] lg:basis-[45%]', isSearchBig ? 'block' : 'hidden']">
        <slot name="title" />
      </div>

      <div
        :class="[
          'relative rounded-xl bg-tarawera-900 text-white duration-300',
          isSearchBig ? 'px-8 py-4 md:basis-[65%] lg:basis-[55%]' : 'md:basis-full'
        ]"
      >
        <Icon
          v-if="advancedTab"
          @click="advancedSearch = !advancedSearch"
          name="circle-down filled"
          size="xl"
          :class="[
            'absolute right-7 rounded-full bg-white text-viking-400 transition-[transform] duration-300 hover:scale-105 hover:cursor-pointer',
            isSearchBig
              ? advancedSearch
                ? '-bottom-3 rotate-180'
                : '-bottom-3 rotate-0'
              : advancedSearch
                ? '-top-3 rotate-0 md:-bottom-3 md:top-auto md:rotate-180'
                : '-top-3 rotate-180 md:-bottom-3 md:top-auto md:rotate-0'
          ]"
        />
        <div :class="['-m-2 grid grid-cols-12 items-center pb-4', isSearchBig ? 'pt-4' : 'pt-6']">
          <div
            :class="[
              'relative order-1 col-span-12 m-2',
              isSearchBig ? 'sm:col-span-12' : advancedSearch ? 'sm:col-span-4' : 'hidden sm:col-span-4 md:block'
            ]"
          >
            <div class="w-full" v-if="sectors.data.length">
              <Listbox class="relative text-tarawera-900" v-model="filters.sector" as="div" multiple>
                <ListboxButton class="form-select w-full" as="div">
                  <div class="text-zinc-400" v-if="!filters.sector.length">Profession</div>
                  <div class="-mx-1 -my-0.5 inline-flex flex-wrap" v-else>
                    <div
                      class="m-0.5 inline-flex cursor-pointer items-center space-x-1 whitespace-nowrap rounded-sm bg-viking-400 px-1 py-0.5 text-sm duration-300 hover:bg-viking-500"
                      v-for="item in filters.sector"
                      :key="item.slug"
                      @click.stop="filters.sector = filters.sector.filter(x => x.slug !== item.slug)"
                    >
                      <span>{{ item.name }}</span>
                      <Icon class="text-white" name="xmark" size="sm" />
                    </div>
                  </div>
                </ListboxButton>
                <ListboxOptions
                  class="absolute z-10 mt-0.5 max-h-64 w-full max-w-full overflow-auto rounded border border-zinc-300 bg-white shadow focus:border-viking-400 focus:outline-none focus:ring-1 focus:ring-viking-400"
                >
                  <ListboxOption
                    class="cursor-default truncate px-2 py-1 hover:bg-zinc-100"
                    v-for="item in sectors.data"
                    v-text="item.name"
                    :class="{
                      'text-zinc-400': filters.sector.includes(item)
                    }"
                    :key="item.slug"
                    :value="item"
                  />
                </ListboxOptions>
              </Listbox>
            </div>
            <div
              class="absolute right-8 top-1/2 -translate-y-1/2 transform space-x-1 text-sm text-zinc-400/75"
              v-if="createJobSearchUrl && filters.search.length"
            >
              <span>Entrer</span>
              <Icon class="translate-y-0.5" name="arrow-turn-down-left" size="sm" />
            </div>
          </div>
          <div
            :class="[
              'relative order-4 col-span-12 m-2 text-tarawera-900',
              isSearchBig
                ? advancedSearch
                  ? 'sm:col-span-6'
                  : 'sm:col-span-8'
                : advancedSearch
                  ? 'sm:col-span-4 md:col-span-4'
                  : 'hidden sm:col-span-6 md:block'
            ]"
            ref="cityDropdownRef"
          >
            <Combobox v-model="filters.location" as="div">
              <div class="relative">
                <ComboboxInput
                  class="form-input w-full pl-8 text-zinc-400"
                  placeholder="Où ?"
                  :displayValue="
                    location => {
                      if (!location) return ''

                      const name = location.name
                      const type = location.type

                      if (type === 'city' && filters.radius) {
                        return `${name} (${filters.radius}km)`
                      } else {
                        return name
                      }
                    }
                  "
                  @change="event => (cityQuery = event.target.value)"
                  @focus="handleCityInputFocus"
                  @blur="handleCityInputBlur"
                  @keydown="handleCityInputKeydown"
                  :readonly="!!filters.location"
                />
                <div class="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">
                  <Icon name="location-dot" size="sm" />
                </div>

                <div class="absolute right-1 top-1/2 flex -translate-y-1/2 items-center">
                  <button
                    class="mr-1 rounded-full p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600"
                    v-if="filters.location"
                    @click="clearCityAndRadius"
                    type="button"
                  >
                    <Icon name="xmark" size="sm" />
                  </button>
                  <ComboboxButton class="form-select h-5/6 w-9 items-center border-0" v-if="!filters.location && !isCityInputFocused" />
                </div>
              </div>

              <ComboboxOptions
                class="absolute z-10 mt-0.5 max-h-64 w-full max-w-full overflow-auto rounded border border-zinc-300 bg-white shadow focus:border-viking-400 focus:outline-none focus:ring-1 focus:ring-viking-400"
              >
                <div class="cursor-default select-none truncate px-2 py-1 text-zinc-500" v-if="citySuggestions.length === 0">Aucun résultat</div>

                <ComboboxOption
                  class="cursor-default truncate px-2 py-1 hover:bg-zinc-100"
                  v-for="location in citySuggestions"
                  :key="location.slug"
                  :value="location"
                >
                  <div class="flex items-center justify-between">
                    <span>{{ location.name }}</span>
                    <span class="ml-2 text-xs text-gray-500" v-if="location.type === 'region'">Région</span>
                    <span class="ml-2 text-xs text-gray-500" v-if="location.type === 'department'">Département</span>
                    <span class="ml-2 text-xs text-gray-500" v-if="location.type === 'city'">Ville</span>
                  </div>
                </ComboboxOption>
              </ComboboxOptions>
            </Combobox>
            <div
              class="absolute z-20 mt-1 w-full rounded-md border border-zinc-300 bg-white p-4 shadow-lg"
              v-if="showRadiusDropdown && filters.location && filters.location.type === 'city'"
            >
              <div
                class="mb-2 inline-flex cursor-pointer items-center space-x-1 whitespace-nowrap rounded-md bg-tarawera-900 px-2 py-1 text-sm text-white"
                @click="clearCityAndRadius"
              >
                <span>{{ filters.location.name }}</span>
                <Icon class="text-white" name="xmark" size="sm" />
              </div>
              <div class="mb-4">
                <div class="flex items-center justify-between">
                  <label class="block text-zinc-500">Dans un rayon de: </label>
                  <span>{{ tempRadius }} km</span>
                </div>
                <input
                  class="w-full appearance-none bg-transparent [&::-webkit-slider-runnable-track]:h-[4px] [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-tarawera-900 [&::-webkit-slider-thumb]:mt-[-6px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-tarawera-900 [&::-webkit-slider-thumb]:bg-tarawera-900"
                  v-model="tempRadius"
                  type="range"
                  min="0"
                  max="100"
                  step="10"
                />
              </div>
              <div class="flex justify-center space-x-3">
                <Button class="bg-white" @click="clearCityAndRadius"> Effacer </Button>
                <Button @click="confirmRadius"> Valider </Button>
              </div>
            </div>
          </div>

          <div
            v-if="employerTypes.data.length"
            v-show="advancedSearch"
            :class="['order-2 col-span-12 m-2', isSearchBig ? 'sm:col-span-6' : 'sm:col-span-4 lg:col-span-4']"
          >
              <Listbox class="relative text-tarawera-900" v-model="filters.employerType" as="div" multiple>
                <ListboxButton class="form-select w-full" as="div">
                  <div class="text-zinc-400" v-if="!filters.employerType.length">Type d’organisation</div>
                  <div class="-mx-1 -my-0.5 inline-flex flex-wrap" v-else>
                    <div
                      class="m-0.5 inline-flex cursor-pointer items-center space-x-1 whitespace-nowrap rounded-sm bg-viking-400 px-1 py-0.5 text-sm duration-300 hover:bg-viking-500"
                      v-for="item in filters.employerType"
                      :key="item.slug"
                      @click.stop="filters.employerType = filters.employerType.filter(x => x.slug !== item.slug)"
                    >
                      <span>{{ item.name }}</span>
                      <Icon class="text-white" name="xmark" size="sm" />
                    </div>
                  </div>
                </ListboxButton>
                <ListboxOptions
                  class="absolute z-10 mt-0.5 max-h-64 w-full max-w-full overflow-auto rounded border border-zinc-300 bg-white shadow focus:border-viking-400 focus:outline-none focus:ring-1 focus:ring-viking-400"
                >
                  <ListboxOption
                    class="cursor-default truncate px-2 py-1 hover:bg-zinc-100"
                    v-for="item in employerTypes.data"
                    v-text="item.name"
                    :class="{
                      'text-zinc-400': filters.employerType.includes(item)
                    }"
                    :key="item.slug"
                    :value="item"
                  />
                </ListboxOptions>
              </Listbox>
          </div>
          
          <div
            v-show="advancedSearch"
            :class="[
              'order-5 flex flex-wrap',
              isSearchBig
                ? 'col-span-12 row-span-3 flex-col  sm:order-3 sm:col-span-6 sm:flex-col'
                : 'order-5 col-span-12 flex-col md:col-span-10 md:justify-around md:flex-row sm:col-span-6'
            ]"
          >
            <div class="m-2 flex items-center justify-between space-x-2">
              <span class="text-nowrap">Catégories :</span>
              <div class="ml-2 flex w-full flex-row space-x-3">
                <div v-for="cat in categories.data" :key="cat.id">
                  <label class="flex items-center space-x-1">
                    <input class="form-checkbox" v-model="filters.category" type="checkbox" :value="cat" />
                    <span>{{ cat.name }}</span>
                  </label>
                </div>
              </div>
            </div>
            <SwitchGroup class="m-2 flex items-center space-x-2" as="div">
              <Switch
                class="relative inline-flex h-6 w-10 shrink-0 items-center rounded-full outline-none duration-300 focus-visible:ring focus-visible:ring-viking-400"
                v-model="filters.teleworkable"
                :class="filters.teleworkable ? 'bg-viking-400' : 'bg-zinc-300'"
              >
                <span
                  class="inline-block h-4 w-4 rounded-full bg-white duration-300"
                  :class="filters.teleworkable ? 'translate-x-5' : 'translate-x-1'"
                />
              </Switch>
              <SwitchLabel class="truncate whitespace-nowrap">Télétravail possible</SwitchLabel>
            </SwitchGroup>

            <SwitchGroup class="m-2 flex items-center space-x-2" as="div">
              <Switch
                class="relative inline-flex h-6 w-10 shrink-0 items-center rounded-full outline-none duration-300 focus-visible:ring focus-visible:ring-viking-400"
                v-model="filters.management"
                :class="filters.management ? 'bg-viking-400' : 'bg-zinc-300'"
              >
                <span
                  class="inline-block h-4 w-4 rounded-full bg-white duration-300"
                  :class="filters.management ? 'translate-x-5' : 'translate-x-1'"
                />
              </Switch>
              <SwitchLabel class="truncate whitespace-nowrap">Management</SwitchLabel>
            </SwitchGroup>

            <SwitchGroup class="m-2 flex items-center space-x-2" as="div">
              <Switch
                class="relative inline-flex h-6 w-10 shrink-0 items-center rounded-full outline-none duration-300 focus-visible:ring focus-visible:ring-viking-400"
                v-model="filters.type"
                :class="filters.type ? 'bg-viking-400' : 'bg-zinc-300'"
              >
                <span class="inline-block h-4 w-4 rounded-full bg-white duration-300" :class="filters.type ? 'translate-x-5' : 'translate-x-1'" />
              </Switch>
              <SwitchLabel class="truncate whitespace-nowrap">Stage / Alternance</SwitchLabel>
            </SwitchGroup>
          </div>
          <slot name="cta" />
        </div>
      </div>
    </div>
  </div>
</template>
